<template>
  <v-dialog
    :value="value"
    max-width="1100px"
    scrollable
    persistent
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-toolbar
        dense
        dark
        color="deep-orange"
        elevation="2"
      >
        <v-toolbar-title>
          Historial de ejecuciones
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <v-btn
          icon
          :disabled="loading"
          @click="cerrar"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-4">
        <div
          v-if="configuracion"
          class="mb-4"
        >
          <div class="subtitle-1 font-weight-medium">
            {{ configuracion.nombre }}
          </div>

          <div class="caption grey--text">
            {{ configuracion.codigo }}
          </div>
        </div>

        <v-data-table
          :headers="headers"
          :items="historial"
          :loading="loading"
          item-key="codigo"
          :footer-props="footerProps"
          class="elevation-1"
        >
          <v-progress-linear
            slot="progress"
            color="deep-orange"
            indeterminate
          ></v-progress-linear>

          <template v-slot:item.estatus="{ item }">
            <v-chip
              x-small
              outlined
              :color="colorEstatus(item.estatus)"
            >
              {{
                nombreEstatus(
                  item.estatus
                )
              }}
            </v-chip>
          </template>

          <template v-slot:item.fecha="{ item }">
            <div>
              {{ formatearFecha(item.fecha_inicio_ejecucion) }}
            </div>

            <div class="caption grey--text">
              {{
                formatearDuracion(
                  item.duracion_ms
                )
              }}
            </div>
          </template>

          <template v-slot:item.periodo="{ item }">
            <div>
              {{
                formatearFechaCorta(
                  item.fecha_inicio_actividad
                )
              }}
            </div>

            <div class="caption grey--text">
              a
              {{
                formatearFechaCorta(
                  item.fecha_fin_actividad
                )
              }}
            </div>
          </template>

          <template v-slot:item.resultados="{ item }">
            {{
              formatearNumero(
                item.cantidad_resultados
              )
            }}
          </template>

          <template v-slot:item.archivo="{ item }">
            <div>
              {{ item.nombre_archivo || "Sin archivo" }}
            </div>

            <div class="caption grey--text">
              {{
                formatearBytes(
                  item.tamano_bytes
                )
              }}
            </div>
          </template>

          <template v-slot:item.error="{ item }">
            <span
              v-if="item.mensaje_error"
              class="error--text"
            >
              {{ item.mensaje_error }}
            </span>

            <span
              v-else
              class="grey--text"
            >
              Sin error
            </span>
          </template>

          <template v-slot:item.acciones="{ item }">
            <v-tooltip
              v-if="item.url"
              bottom
            >
              <template
                v-slot:activator="{ on, attrs }"
              >
                <v-btn
                  icon
                  small
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="abrirArchivo(item.url)"
                >
                  <v-icon small>
                    mdi-open-in-new
                  </v-icon>
                </v-btn>
              </template>

              <span>
                Abrir archivo en OneDrive
              </span>
            </v-tooltip>
          </template>

          <template v-slot:no-data>
            No hay ejecuciones registradas
          </template>
        </v-data-table>

        <v-alert
          v-if="error"
          type="error"
          dense
          outlined
          class="mt-4"
        >
          {{ mensajeError }}
        </v-alert>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn
          text
          :disabled="loading"
          @click="cerrar"
        >
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import {
  listarHistorialExportacion,
} from "@/utils/exportacionesProgramadasApi";

export default {
  name: "HistorialExportacionDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },

    configuracion: {
      type: Object,
      default: null,
    },
  },

  data: () => ({
    loading: false,
    error: false,
    mensajeError: "",
    historial: [],

    footerProps: {
      "items-per-page-text":
        "Elementos por página",
      "items-per-page-options":
        [10, 25, 50, -1],
    },

    headers: [
      {
        text: "Estado",
        value: "estatus",
        align: "center",
      },
      {
        text: "Origen",
        value: "origen",
        align: "center",
      },
      {
        text: "Ejecución",
        value: "fecha",
        align: "center",
        sortable: false,
      },
      {
        text: "Periodo de registro",
        value: "periodo",
        align: "center",
        sortable: false,
      },
      {
        text: "Resultados",
        value: "resultados",
        align: "center",
        sortable: false,
      },
      {
        text: "Archivo",
        value: "archivo",
        align: "start",
        sortable: false,
      },
      {
        text: "Error",
        value: "error",
        align: "start",
        sortable: false,
      },
      {
        text: "Acciones",
        value: "acciones",
        align: "center",
        sortable: false,
      },
    ],
  }),

  watch: {
    value(valor) {
      if (valor) {
        this.cargar();
      }
    },
  },

  methods: {
    async cargar() {
      this.error = false;
      this.mensajeError = "";
      this.historial = [];

      if (
        !this.configuracion ||
        !this.configuracion.codigo
      ) {
        this.mostrarError(
          "No se recibió una configuración válida"
        );
        return;
      }

      this.loading = true;

      try {
        const resultado =
          await listarHistorialExportacion(
            this.$session,
            this.configuracion.codigo
          );

        if (!resultado.correcto) {
          this.mostrarError(
            resultado.mensaje ||
              "No fue posible consultar el historial"
          );
          return;
        }

        this.historial =
          Array.isArray(resultado.historial)
            ? resultado.historial
            : [];
      } catch (error) {
        console.error(error);

        this.mostrarError(
          error.response &&
          error.response.data &&
          error.response.data.mensaje
            ? error.response.data.mensaje
            : "No fue posible consultar el historial"
        );
      } finally {
        this.loading = false;
      }
    },

    cerrar() {
      if (this.loading) {
        return;
      }

      this.$emit("input", false);
    },

    mostrarError(mensaje) {
      this.error = true;
      this.mensajeError = mensaje;
    },

    formatearFecha(fecha) {
      if (!fecha) {
        return "Sin fecha";
      }

      const valor = moment(fecha);

      return valor.isValid()
        ? valor.format("DD/MM/YYYY hh:mm A")
        : fecha;
    },

    formatearFechaCorta(fecha) {
      if (!fecha) {
        return "Sin fecha";
      }

      const valor = moment(
        fecha,
        "YYYY-MM-DD",
        true
      );

      return valor.isValid()
        ? valor.format("DD/MM/YYYY")
        : fecha;
    },

    formatearNumero(valor) {
      return Number(valor || 0)
        .toLocaleString("es-MX");
    },

    formatearDuracion(valor) {
      const milisegundos =
        Number(valor || 0);

      if (milisegundos < 1000) {
        return `${milisegundos} ms`;
      }

      return `${(
        milisegundos / 1000
      ).toFixed(1)} s`;
    },

    formatearBytes(valor) {
      const bytes = Number(valor || 0);

      if (!bytes) {
        return "0 B";
      }

      const unidades = [
        "B",
        "KB",
        "MB",
        "GB",
      ];

      const indice = Math.min(
        Math.floor(
          Math.log(bytes) /
          Math.log(1024)
        ),
        unidades.length - 1
      );

      const cantidad =
        bytes / Math.pow(1024, indice);

      return (
        cantidad.toFixed(
          indice === 0 ? 0 : 1
        ) +
        " " +
        unidades[indice]
      );
    },

    nombreEstatus(estatus) {
      const nombres = {
        exitosa: "Exitosa",
        parcial: "Parcial",
        error: "Error",
        fallida: "Fallida",
        en_proceso: "En proceso",
        sin_datos: "Sin datos",
      };

      return nombres[estatus] || estatus;
    },

    colorEstatus(estatus) {
      if (estatus === "exitosa") {
        return "success";
      }

      if (
        estatus === "error" ||
        estatus === "fallida"
      ) {
        return "error";
      }

      if (
        estatus === "en_proceso" ||
        estatus === "sin_datos"
      ) {
        return "info";
      }

      return "grey";
    },

    abrirArchivo(url) {
      if (!url) {
        return;
      }

      window.open(
        url,
        "_blank",
        "noopener,noreferrer"
      );
    },
  },
};
</script>
